import { useState, useEffect, useCallback } from 'react';
import { requireServerAnswer } from '../../../utils/serverRead';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Hook to fetch all user's projects that have user testing configured
 * Returns projects sorted by last activity (most recent first)
 */
export function useTestingProjects(userId) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProjects = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const db = firebase.firestore();
      const urlsRef = db.collection('user').doc(userId).collection('url');
      const urlsSnapshot = requireServerAnswer(await urlsRef.orderBy('createdAt', 'desc').get());

      // Process each project
      const promises = urlsSnapshot.docs.map(async (doc) => {
        const data = doc.data();

        // Include configured tests and in-progress test drafts. A test draft
        // always carries a userTesting object (the CreateFlow contract), which
        // also keeps site drafts (no userTesting) out of the Tests list.
        const ut = data.userTesting;
        const isConfiguredTest = ut && (ut.enabled === true || ut.status);
        const isTestDraft = data.draft === true && !!ut;
        if (!isConfiguredTest && !isTestDraft) {
          return null;
        }

        // Heal an agent-created test draft: create_user_test set
        // userTesting.enabled server-side but could not clear the client-only
        // draft flag, so it would otherwise stay an in-progress draft. A
        // configured test is no longer a draft. (Client finishes clear draft
        // atomically, so only the agent path reaches here.)
        let isDraft = data.draft === true;
        if (isDraft && ut && ut.enabled === true) {
          isDraft = false;
          doc.ref.set(
            { draft: firebase.firestore.FieldValue.delete() },
            { merge: true }
          ).catch((e) => console.error('Draft heal failed:', e));
        }

        // Get session count
        const sessionsSnapshot = await doc.ref.collection('testingSessions').get();
        const sessionCount = sessionsSnapshot.size;

        // Calculate completion rate
        let completedTasks = 0;
        let totalTasks = 0;

        sessionsSnapshot.docs.forEach((sessionDoc) => {
          const sessionData = sessionDoc.data();
          if (sessionData.taskResults) {
            sessionData.taskResults.forEach((result) => {
              totalTasks++;
              if (result.status === 'done') {
                completedTasks++;
              }
            });
          }
        });

        // The most recent sessions, light fields only, for Home's "What's new"
        // and session dots (additive: read from the snapshot already loaded
        // above, no extra query).
        const asDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
        const recentSessions = sessionsSnapshot.docs
          .map((sessionDoc) => {
            const sd = sessionDoc.data();
            return {
              id: sessionDoc.id,
              status: sd.status || null,
              startedAt: asDate(sd.startedAt),
              completedAt: asDate(sd.completedAt),
              testerName: sd.testerName || null,
              hasAudio: !!sd.audioUrl,
              transcriptStatus: sd.transcriptStatus || null,
              analysisStatus: sd.analysisStatus || null,
              isOwnerPreview: sd.isOwnerPreview === true,
            };
          })
          .sort((x, y) => ((y.completedAt || y.startedAt || 0) - (x.completedAt || x.startedAt || 0)))
          .slice(0, 6);

        const completionRate = totalTasks > 0
          ? Math.round((completedTasks / totalTasks) * 100)
          : 0;

        // Get last session date
        let lastSessionAt = null;
        if (sessionCount > 0) {
          const lastSessionQuery = await doc.ref
            .collection('testingSessions')
            .orderBy('completedAt', 'desc')
            .limit(1)
            .get();

          if (!lastSessionQuery.empty) {
            const lastSession = lastSessionQuery.docs[0].data();
            lastSessionAt = lastSession.completedAt?.toDate?.() || lastSession.completedAt;
          }
        }

        return {
          id: doc.id,
          title: data.title || 'Untitled Project',
          generatedUrl: data.generatedUrl,
          type: data.type || null,
          customDomain: data.domain,
          previewUrl: data.previewUrl || null,
          previewColors: Array.isArray(data.previewColors) ? data.previewColors : null,
          userTesting: data.userTesting,
          draft: isDraft,
          draftMeta: data.draftMeta || null,
          sessionCount,
          recentSessions,
          completionRate,
          lastSessionAt,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt
        };
      });

      const results = await Promise.all(promises);

      // Filter null values and sort by last activity
      const validProjects = results.filter(Boolean);
      validProjects.sort((a, b) => {
        const aDate = a.lastSessionAt || a.updatedAt || a.createdAt;
        const bDate = b.lastSessionAt || b.updatedAt || b.createdAt;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(bDate) - new Date(aDate);
      });

      setProjects(validProjects);
    } catch (err) {
      console.error('Error fetching testing projects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const deleteProject = useCallback(async (projectId) => {
    try {
      const functions = getFunctions();
      const deleteTestProject = httpsCallable(functions, 'deleteTestProject');
      const result = await deleteTestProject({ projectId });

      if (result.data.success) {
        // Remove from local state
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        return { success: true };
      } else {
        return { success: false, error: result.data.error };
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const duplicateProject = useCallback(async (projectId) => {
    if (!userId || !projectId) return { success: false };
    try {
      const db = firebase.firestore();
      const sourceSnap = await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .get();
      if (!sourceSnap.exists) {
        return { success: false, error: 'Project not found' };
      }
      const data = sourceSnap.data();
      // Full userTesting config with its status reset to draft. Sessions, the
      // synthesis doc, and customDomain are never copied.
      const utConfig = { ...(data.userTesting || {}), status: 'draft' };

      const newDoc = await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .add({
          title: 'Copy of ' + (data.title || 'Untitled Project'),
          userId,
          userTesting: utConfig,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

      // Mirror the pluginUserTesting bridge a figma test writes at creation
      // so the Figma plugin can find the duplicate.
      await db.collection('pluginUserTesting').doc(newDoc.id).set({
        figmaUrl: utConfig.prototypeUrl || null,
        userTesting: utConfig,
        source: 'dashboard',
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        ownerId: userId,
      });

      await fetchProjects();
      return { success: true, id: newDoc.id };
    } catch (err) {
      console.error('Error duplicating project:', err);
      return { success: false, error: err.message };
    }
  }, [userId, fetchProjects]);

  const renameProject = useCallback(async (projectId, newTitle) => {
    if (!userId || !projectId) return { success: false };
    const trimmed = (newTitle || '').trim();
    if (!trimmed) return { success: false, error: 'Title cannot be empty' };
    try {
      const db = firebase.firestore();
      await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .update({ title: trimmed, updatedAt: new Date() });

      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, title: trimmed } : p))
      );
      return { success: true };
    } catch (err) {
      console.error('Error renaming project:', err);
      return { success: false, error: err.message };
    }
  }, [userId]);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    deleteProject,
    renameProject,
    duplicateProject
  };
}

export default useTestingProjects;
